import { Template, ImageElement, TextElement, ShapeElement, IconElement, IconName, GradientStop, PolygonPoint, isPastelUri, parsePastelUri, DYNAMIC_TEXT_LIMITS, DynamicTextType } from '@agency-smm/shared';
import { ICON_SVGS } from './iconSvgs';
import { ensureTemplateFontsLoaded, ensureFontLoaded } from './fontLoader';
import { buildBrandPalette, generateAdaptiveColors, applyPaletteToElement } from './applyBrandColors';

// Image cache to avoid reloading the same images multiple times
const imageCache = new Map<string, HTMLImageElement>();

// API base URL for proxying images
const API_BASE_URL = import.meta.env?.VITE_API_URL || 'https://agency-smm-api.carlos-mdt-1057.workers.dev';

/**
 * Load an image with optional proxy support and caching
 */
export const loadImage = (url: string, useProxy: boolean = true): Promise<HTMLImageElement> => {
  // Check cache first
  const cacheKey = useProxy ? `proxy:${url}` : url;
  const cached = imageCache.get(cacheKey);
  if (cached) {
    return Promise.resolve(cached);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      imageCache.set(cacheKey, img);
      resolve(img);
    };

    img.onerror = () => {
      if (useProxy) {
        // Try without proxy as fallback
        loadImage(url, false)
          .then(resolve)
          .catch(reject);
      } else {
        reject(new Error('Failed to load image'));
      }
    };

    if (useProxy) {
      img.src = `${API_BASE_URL}/api/proxy/image?url=${encodeURIComponent(url)}`;
    } else {
      img.src = url;
    }
  });
};

/**
 * Clear the image cache (useful for memory management)
 */
export const clearImageCache = () => {
  imageCache.clear();
};

/**
 * Create a canvas gradient from gradient definition
 */
const createGradient = (
  ctx: CanvasRenderingContext2D,
  type: string,
  colors: GradientStop[],
  angle: number,
  width: number,
  height: number
): CanvasGradient => {
  let gradient: CanvasGradient;

  if (type === 'radial') {
    gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, Math.max(width, height) / 2);
  } else {
    // Linear gradient: convert angle (degrees) to start/end points
    const rad = ((angle - 90) * Math.PI) / 180;
    const hw = width / 2;
    const hh = height / 2;
    const x1 = -Math.cos(rad) * hw;
    const y1 = -Math.sin(rad) * hh;
    const x2 = Math.cos(rad) * hw;
    const y2 = Math.sin(rad) * hh;
    gradient = ctx.createLinearGradient(x1, y1, x2, y2);
  }

  for (const stop of colors) {
    gradient.addColorStop(Math.max(0, Math.min(1, stop.stop)), stop.color);
  }

  return gradient;
};

/**
 * Build a centered parallelogram path at the current origin.
 * The visual centroid stays at (0, 0): the top edge slides right by offset/2
 * and the bottom edge slides left by offset/2, so rotation/positioning feel natural.
 * skewAngle is clamped to [-45, 45] degrees to avoid degenerate shapes.
 */
const drawParallelogramPath = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  skewAngle: number
): void => {
  const clamped = Math.max(-45, Math.min(45, skewAngle || 0));
  const offset = Math.tan((clamped * Math.PI) / 180) * height;
  const halfOffset = offset / 2;
  const hw = width / 2;
  const hh = height / 2;
  ctx.beginPath();
  ctx.moveTo(-hw + halfOffset, -hh);   // top-left
  ctx.lineTo(hw + halfOffset, -hh);    // top-right
  ctx.lineTo(hw - halfOffset, hh);     // bottom-right
  ctx.lineTo(-hw - halfOffset, hh);    // bottom-left
  ctx.closePath();
};

/**
 * Render a curated library icon to an HTMLImageElement with the given color.
 * The SVG string is fetched from ICON_SVGS (auto-generated from lucide-static),
 * color is injected by replacing `currentColor` references, then wrapped in a
 * blob URL and loaded as an image. Result is cached per (iconName, color).
 */
const iconImageCache = new Map<string, Promise<HTMLImageElement>>();
const loadIconImage = (iconName: IconName, color: string): Promise<HTMLImageElement> => {
  const cacheKey = `${iconName}::${color}`;
  const cached = iconImageCache.get(cacheKey);
  if (cached) return cached;

  const raw = ICON_SVGS[iconName];
  if (!raw) return Promise.reject(new Error(`Unknown icon: ${iconName}`));

  // Inject color: lucide uses stroke="currentColor" / fill sometimes
  const colored = raw
    .replace(/currentColor/g, color)
    .replace(/stroke="[^"]*"/g, `stroke="${color}"`);

  const blob = new Blob([colored], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load icon SVG')); };
    img.src = url;
  });
  iconImageCache.set(cacheKey, promise);
  return promise;
};

/**
 * Build a polygon path from a list of PolygonPoint (coords in 0..100 of the
 * element bounding box). Origin is the element center (matching the other
 * shape draw helpers in this file). 3+ points required; invalid input draws
 * nothing. Points outside 0..100 are allowed (they just extend past the box).
 */
const drawPolygonPath = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  points: PolygonPoint[]
): void => {
  if (!points || points.length < 3) {
    ctx.beginPath();
    return;
  }
  const toX = (p: PolygonPoint) => (p.x / 100) * width - width / 2;
  const toY = (p: PolygonPoint) => (p.y / 100) * height - height / 2;
  ctx.beginPath();
  ctx.moveTo(toX(points[0]), toY(points[0]));
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(toX(points[i]), toY(points[i]));
  }
  ctx.closePath();
};

/**
 * Wrap text to fit within maxWidth - matches CSS word-wrap behavior.
 * Honors user-inserted line breaks (\n) as HARD breaks: each paragraph is
 * wrapped independently, then concatenated. Empty lines from \n\n are
 * preserved as blank lines so the user can do paragraph spacing.
 * Does NOT change font size, only wraps text to multiple lines.
 */
const wrapTextToWidth = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  wrapStyle: string = 'normal'
): string[] => {
  // Normalize line endings then split on hard breaks. Each "paragraph" is
  // wrapped on its own so user-typed newlines act as a real line break.
  const paragraphs = text.replace(/\r\n/g, '\n').split('\n');

  // nowrap: respect newlines but don't soft-wrap each paragraph.
  if (wrapStyle === 'nowrap') {
    return paragraphs;
  }

  const wrapParagraph = (para: string): string[] => {
    if (para === '') return [''];

    const lines: string[] = [];

    // break-all: break at any character
    if (wrapStyle === 'break-all') {
      let currentLine = '';
      for (const char of para) {
        const testLine = currentLine + char;
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = char;
        } else {
          currentLine = testLine;
        }
      }
      if (currentLine) lines.push(currentLine);
      return lines;
    }

    // normal or break-word: wrap at word boundaries
    const words = para.split(' ');
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);

      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);

        // For break-word, break long words that don't fit
        if (wrapStyle === 'break-word') {
          const wordMetrics = ctx.measureText(word);
          if (wordMetrics.width > maxWidth) {
            // Break the word character by character
            let wordPart = '';
            for (const char of word) {
              const partMetrics = ctx.measureText(wordPart + char);
              if (partMetrics.width > maxWidth && wordPart) {
                lines.push(wordPart);
                wordPart = char;
              } else {
                wordPart += char;
              }
            }
            currentLine = wordPart;
          } else {
            currentLine = word;
          }
        } else {
          currentLine = word;
        }
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);

    return lines;
  };

  // Wrap each paragraph independently, then flatten.
  const all: string[] = [];
  for (const para of paragraphs) {
    all.push(...wrapParagraph(para));
  }
  return all;
};

/**
 * Render text on canvas with auto-shrink, letter spacing, and configurable line height
 *
 * Behavior:
 * - display: flex; align-items: center; (vertical center)
 * - justify-content based on textAlign (horizontal alignment)
 * - When autoSize=true: font shrinks (binary search) until text fits bounding box
 * - text ALWAYS wraps based on wrapStyle (normal, break-word, break-all, nowrap)
 */
export const renderText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  elementWidth: number,
  elementHeight: number,
  fontFamily: string,
  fontSize: number,
  fontWeight: string,
  textColor: string,
  textAlign: CanvasTextAlign,
  autoSize: boolean,
  wrapStyle: string,
  outlineColor?: string,
  outlineWidth?: number,
  letterSpacing?: number,
  lineHeightMultiplier?: number,
  textShadowEnabled?: boolean
) => {
  const lhMultiplier = lineHeightMultiplier ?? 1.3;

  // Auto-shrink: find the largest font size that fits the bounding box
  let effectiveFontSize = fontSize;
  if (autoSize && wrapStyle !== 'nowrap') {
    let lo = 8; // minimum readable size
    let hi = fontSize;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      ctx.font = `${fontWeight} ${mid}px ${fontFamily}`;
      const testLines = wrapTextToWidth(ctx, text, elementWidth, wrapStyle);
      const testHeight = testLines.length * mid * lhMultiplier;
      if (testHeight <= elementHeight) {
        effectiveFontSize = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
  }

  // Set final font
  ctx.font = `${fontWeight} ${effectiveFontSize}px ${fontFamily}`;
  ctx.textAlign = textAlign;
  ctx.textBaseline = 'middle';

  // Apply letter spacing if supported
  if (letterSpacing && 'letterSpacing' in ctx) {
    (ctx as unknown as Record<string, string>).letterSpacing = `${letterSpacing}px`;
  }

  // Controllable text shadow (default: enabled for backward compat)
  const shadowOn = textShadowEnabled !== false;
  if (shadowOn) {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;
  } else {
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }

  // Wrap text at the effective font size
  let lines: string[];
  if (wrapStyle !== 'nowrap') {
    lines = wrapTextToWidth(ctx, text, elementWidth, wrapStyle);
  } else {
    lines = [text];
  }

  const lineHeight = effectiveFontSize * lhMultiplier;
  const totalTextHeight = lines.length * lineHeight;

  // Vertical centering (like CSS align-items: center)
  const startY = -totalTextHeight / 2 + lineHeight / 2;

  // Horizontal position based on alignment (like CSS justify-content)
  let xOffset = 0;
  if (textAlign === 'left') {
    xOffset = -elementWidth / 2;
  } else if (textAlign === 'right') {
    xOffset = elementWidth / 2;
  }
  // center: xOffset stays 0

  // Effective outline width scaled proportionally if font was shrunk
  const effectiveOutlineWidth = outlineWidth
    ? outlineWidth * (effectiveFontSize / fontSize)
    : undefined;

  // Draw each line
  lines.forEach((line, index) => {
    const y = startY + index * lineHeight;

    // When autoSize is ON, clip lines outside element bounds
    if (autoSize) {
      const lineTop = y - lineHeight / 2;
      const lineBottom = y + lineHeight / 2;
      if (lineBottom < -elementHeight / 2 || lineTop > elementHeight / 2) {
        return;
      }
    }

    // Draw outline first if specified (like CSS -webkit-text-stroke)
    if (outlineColor && effectiveOutlineWidth && effectiveOutlineWidth > 0) {
      ctx.strokeStyle = outlineColor;
      ctx.lineWidth = effectiveOutlineWidth * 2;
      ctx.lineJoin = 'round';
      ctx.strokeText(line, xOffset, y);
    }

    // Draw fill on top (like CSS paint-order: stroke fill)
    ctx.fillStyle = textColor;
    ctx.fillText(line, xOffset, y);
  });

  // Reset letter spacing
  if (letterSpacing && 'letterSpacing' in ctx) {
    (ctx as unknown as Record<string, string>).letterSpacing = '0px';
  }
};

/**
 * Export quality presets for ZIP downloads.
 * Trades file size for resolution / fidelity.
 */
export type ExportQuality = 'original' | 'mobile' | 'low';

export interface ExportPreset {
  scale: number;
  format: 'image/png' | 'image/jpeg';
  quality: number;
  extension: 'png' | 'jpg';
  label: string;
}

export const EXPORT_PRESETS: Record<ExportQuality, ExportPreset> = {
  original: {
    scale: 1.0,
    format: 'image/png',
    quality: 1.0,
    extension: 'png',
    label: 'Original (PNG, full quality)',
  },
  mobile: {
    scale: 0.5,
    format: 'image/jpeg',
    quality: 0.85,
    extension: 'jpg',
    label: 'Mobile (JPG, 50% size)',
  },
  low: {
    scale: 0.4,
    format: 'image/jpeg',
    quality: 0.7,
    extension: 'jpg',
    label: 'Low (JPG, smallest)',
  },
};

export interface RenderOptions {
  /** Dynamic texts to override template element content, keyed by element ID */
  dynamicTexts?: Record<string, string>;
  /** Scale factor for thumbnail generation (1.0 = full size) */
  scale?: number;
  /** Background image horizontal offset (-100 to 100, percentage from center) */
  backgroundOffsetX?: number;
  /** Background image vertical offset (-100 to 100, percentage from center) */
  backgroundOffsetY?: number;
  /** Background image scale multiplier (1.0 = default cover fit, >1 = zoom in, <1 = zoom out) */
  backgroundScale?: number;
  /** Resolved workspace logo URL for 'workspace_logo' reference elements */
  workspaceLogoUrl?: string;
  /** Resolved post image URL for 'post_image' reference elements (the post's photo) */
  postImageUrl?: string;
  /** Scale multiplier for workspace logo (0.1-3.0, default 1.0) */
  logoScale?: number;
  /** Logo horizontal offset (-50 to 50, percentage of element width) */
  logoOffsetX?: number;
  /** Logo vertical offset (-50 to 50, percentage of element height) */
  logoOffsetY?: number;
  /** Brand colors from brand profile for dynamic color theming */
  brandColors?: string[];
  /** Brand fonts from brand profile for dynamic font theming */
  brandFonts?: string[];
  /** Color mode controlling how template colors are resolved */
  colorMode?: 'fixed' | 'brand' | 'adaptive';
  /** Deterministic seed derived from post ID for consistent random choices */
  postSeed?: number;
}

/** Region where background image should be drawn (for split layouts) */
interface ClipRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Draw background (either pastel color or stock image)
 * @param offsetX - Horizontal pan offset (-100 to 100, percentage of available pan range)
 * @param offsetY - Vertical pan offset (-100 to 100, percentage of available pan range)
 * @param clipRegion - If provided, image is clipped and fit to this region (split layouts)
 */
const drawBackground = async (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  imageUrl: string,
  backgroundColor: string,
  offsetX: number = 0,
  offsetY: number = 0,
  clipRegion?: ClipRegion,
  bgScale: number = 1.0
) => {
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (isPastelUri(imageUrl)) {
    const pastelColor = parsePastelUri(imageUrl);
    ctx.fillStyle = pastelColor || '#F0F0F0';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  } else {
    const baseImage = await loadImage(imageUrl);

    // Target area: either the clip region or the full canvas
    const targetX = clipRegion ? clipRegion.x : 0;
    const targetY = clipRegion ? clipRegion.y : 0;
    const targetW = clipRegion ? clipRegion.width : canvas.width;
    const targetH = clipRegion ? clipRegion.height : canvas.height;

    const imageAspect = baseImage.width / baseImage.height;
    const targetAspect = targetW / targetH;

    let drawWidth: number, drawHeight: number, drawX: number, drawY: number;

    // Base scale: fit-cover (fill the target area completely)
    // Then apply user's backgroundScale on top (1.0 = cover, >1 = zoom in, <1 = zoom out)
    const userScale = Math.max(0.25, Math.min(3, bgScale));

    if (imageAspect > targetAspect) {
      // Image is wider - fit by height, crop sides
      drawHeight = targetH * userScale;
      drawWidth = baseImage.width * (drawHeight / baseImage.height);
    } else {
      // Image is taller - fit by width, crop top/bottom
      drawWidth = targetW * userScale;
      drawHeight = baseImage.height * (drawWidth / baseImage.width);
    }

    // Center the image in the target area
    drawX = targetX + (targetW - drawWidth) / 2;
    drawY = targetY + (targetH - drawHeight) / 2;

    // Apply pan offsets on both axes independently
    const maxPanX = Math.max(0, (drawWidth - targetW) / 2);
    const maxPanY = Math.max(0, (drawHeight - targetH) / 2);
    if (maxPanX > 0) drawX += (offsetX / 100) * maxPanX;
    if (maxPanY > 0) drawY += (offsetY / 100) * maxPanY;

    // Clip to target region if provided
    if (clipRegion) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(targetX, targetY, targetW, targetH);
      ctx.clip();
    }

    ctx.drawImage(baseImage, drawX, drawY, drawWidth, drawHeight);

    if (clipRegion) {
      ctx.restore();
    }
  }
};

/**
 * Draw template elements on canvas
 */
const drawTemplateElements = async (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  template: Template,
  options: RenderOptions = {}
) => {
  const { dynamicTexts, scale = 1 } = options;

  if (!template.elements.length) return;

  const sortedElements = [...template.elements].sort((a, b) => a.zIndex - b.zIndex);

  // Apply brand colors if color mode is brand or adaptive
  let processedElements = sortedElements;
  if ((options.colorMode === 'brand' || options.colorMode === 'adaptive') && options.brandColors?.length) {
    const palette = buildBrandPalette(options.brandColors);
    if (palette) {
      const effectivePalette = options.colorMode === 'adaptive'
        ? generateAdaptiveColors(palette, options.postSeed || 0)
        : palette;
      processedElements = sortedElements.map(el => applyPaletteToElement(el, effectivePalette));
    }
  }

  for (const element of processedElements) {
    const x = (element.transform.positionX / 100) * canvas.width;
    const y = (element.transform.positionY / 100) * canvas.height;
    const width = (element.transform.width / 100) * canvas.width;
    const height = (element.transform.height / 100) * canvas.height;

    ctx.save();
    ctx.globalAlpha = element.opacity;
    ctx.translate(x, y);

    if (element.transform.rotation) {
      ctx.rotate((element.transform.rotation * Math.PI) / 180);
    }

    // Mirror element via scale around its center. Done after rotation so the
    // flip mirrors what the user visually sees, not the unrotated bounds.
    const fh = element.transform.flipHorizontal ? -1 : 1;
    const fv = element.transform.flipVertical ? -1 : 1;
    if (fh !== 1 || fv !== 1) {
      ctx.scale(fh, fv);
    }

    if (element.elementType === 'image') {
      const imgElement = element as ImageElement;
      const isWorkspaceLogo = imgElement.assetReferenceType === 'workspace_logo';
      const isPostImage = imgElement.assetReferenceType === 'post_image';

      // Resolve image URL based on reference type
      let resolvedUrl = imgElement.imageUrl;
      if (isWorkspaceLogo) {
        resolvedUrl = options.workspaceLogoUrl || undefined;
      } else if (isPostImage) {
        resolvedUrl = options.postImageUrl || undefined;
      }

      if (resolvedUrl) {
        try {
          const overlayImage = await loadImage(resolvedUrl);

          // object-fit: post_image uses 'cover' (fill the element, crop if needed)
          // so photo zones don't letterbox. Other image types use 'contain'.
          const imgAspect = overlayImage.width / overlayImage.height;
          const elementAspect = width / height;

          let drawWidth: number;
          let drawHeight: number;

          if (isPostImage) {
            // cover: scale so the smaller dimension fills the element
            if (imgAspect > elementAspect) {
              drawHeight = height;
              drawWidth = height * imgAspect;
            } else {
              drawWidth = width;
              drawHeight = width / imgAspect;
            }
          } else if (imgAspect > elementAspect) {
            // contain: fit to width
            drawWidth = width;
            drawHeight = width / imgAspect;
          } else {
            // contain: fit to height
            drawHeight = height;
            drawWidth = height * imgAspect;
          }

          // Apply per-workspace logo scale if this is a workspace logo
          if (isWorkspaceLogo && options.logoScale && options.logoScale !== 1.0) {
            drawWidth *= options.logoScale;
            drawHeight *= options.logoScale;
          }

          // Center the image within the element bounds, apply logo offset
          let logoDrawX = -drawWidth / 2;
          let logoDrawY = -drawHeight / 2;
          if (isWorkspaceLogo) {
            if (options.logoOffsetX) logoDrawX += (width * options.logoOffsetX / 100);
            if (options.logoOffsetY) logoDrawY += (height * options.logoOffsetY / 100);
          }

          // Apply clip mask if the image element specifies one
          const clipShape = imgElement.clipShape;
          const clipped = clipShape === 'parallelogram' || clipShape === 'polygon';
          if (clipped) {
            ctx.save();
            if (clipShape === 'parallelogram') {
              drawParallelogramPath(ctx, width, height, imgElement.clipSkewAngle || 0);
            } else {
              drawPolygonPath(ctx, width, height, imgElement.clipPolygonPoints || []);
            }
            ctx.clip();
          }

          ctx.drawImage(
            overlayImage,
            logoDrawX,
            logoDrawY,
            drawWidth,
            drawHeight
          );

          if (clipped) {
            ctx.restore();
          }
        } catch (err) {
          console.error('Failed to load overlay image:', err);
        }
      } else if (imgElement.assetReferenceType === 'workspace_logo') {
        // Draw placeholder for missing workspace logo
        ctx.fillStyle = 'rgba(128, 128, 128, 0.3)';
        ctx.fillRect(-width / 2, -height / 2, width, height);
        ctx.fillStyle = '#999';
        ctx.font = `bold ${Math.max(12, width * 0.15)}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('LOGO', 0, 0);
      } else if (imgElement.assetReferenceType === 'post_image') {
        // Draw placeholder for missing post image
        ctx.fillStyle = 'rgba(80, 80, 80, 0.5)';
        ctx.fillRect(-width / 2, -height / 2, width, height);
        ctx.fillStyle = '#aaa';
        ctx.font = `bold ${Math.max(14, width * 0.06)}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('POST IMAGE', 0, 0);
      }
    } else if (element.elementType === 'shape') {
      const shapeElement = element as ShapeElement;
      const shapeType = shapeElement.shapeType || 'rectangle';

      // Determine fill style: gradient or solid
      if (shapeElement.gradientType && shapeElement.gradientType !== 'none' && shapeElement.gradientColors?.length) {
        ctx.fillStyle = createGradient(ctx, shapeElement.gradientType, shapeElement.gradientColors, shapeElement.gradientAngle || 0, width, height);
      } else {
        ctx.fillStyle = shapeElement.backgroundColor;
      }

      // Apply shadow if specified
      if (shapeElement.shadowColor && (shapeElement.shadowBlur || shapeElement.shadowOffsetX || shapeElement.shadowOffsetY)) {
        ctx.shadowColor = shapeElement.shadowColor;
        ctx.shadowBlur = (shapeElement.shadowBlur || 0) * scale;
        ctx.shadowOffsetX = (shapeElement.shadowOffsetX || 0) * scale;
        ctx.shadowOffsetY = (shapeElement.shadowOffsetY || 0) * scale;
      }

      // Draw shape based on type
      if (shapeType === 'circle' || shapeType === 'ellipse') {
        ctx.beginPath();
        if (shapeType === 'circle') {
          const radius = Math.min(width, height) / 2;
          ctx.arc(0, 0, radius, 0, Math.PI * 2);
        } else {
          ctx.ellipse(0, 0, width / 2, height / 2, 0, 0, Math.PI * 2);
        }
        ctx.fill();
      } else if (shapeType === 'parallelogram') {
        drawParallelogramPath(ctx, width, height, shapeElement.skewAngle || 0);
        ctx.fill();
      } else if (shapeType === 'polygon') {
        drawPolygonPath(ctx, width, height, shapeElement.polygonPoints || []);
        ctx.fill();
      } else {
        // Rectangle (with optional border-radius)
        const scaledRadius = shapeElement.borderRadius * scale;
        if (scaledRadius > 0 && typeof ctx.roundRect === 'function') {
          ctx.beginPath();
          ctx.roundRect(-width / 2, -height / 2, width, height, scaledRadius);
          ctx.fill();
        } else if (scaledRadius > 0) {
          const r = Math.min(scaledRadius, width / 2, height / 2);
          const x0 = -width / 2;
          const y0 = -height / 2;
          ctx.beginPath();
          ctx.moveTo(x0 + r, y0);
          ctx.lineTo(x0 + width - r, y0);
          ctx.arcTo(x0 + width, y0, x0 + width, y0 + r, r);
          ctx.lineTo(x0 + width, y0 + height - r);
          ctx.arcTo(x0 + width, y0 + height, x0 + width - r, y0 + height, r);
          ctx.lineTo(x0 + r, y0 + height);
          ctx.arcTo(x0, y0 + height, x0, y0 + height - r, r);
          ctx.lineTo(x0, y0 + r);
          ctx.arcTo(x0, y0, x0 + r, y0, r);
          ctx.closePath();
          ctx.fill();
        } else {
          ctx.fillRect(-width / 2, -height / 2, width, height);
        }
      }

      // Draw border/stroke if specified
      if (shapeElement.borderColor && shapeElement.borderWidth && shapeElement.borderWidth > 0) {
        // Reset shadow before stroke to avoid double-shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.strokeStyle = shapeElement.borderColor;
        ctx.lineWidth = shapeElement.borderWidth * scale;
        if (shapeType === 'circle' || shapeType === 'ellipse') {
          ctx.beginPath();
          if (shapeType === 'circle') {
            ctx.arc(0, 0, Math.min(width, height) / 2, 0, Math.PI * 2);
          } else {
            ctx.ellipse(0, 0, width / 2, height / 2, 0, 0, Math.PI * 2);
          }
          ctx.stroke();
        } else if (shapeType === 'parallelogram') {
          drawParallelogramPath(ctx, width, height, shapeElement.skewAngle || 0);
          ctx.stroke();
        } else if (shapeType === 'polygon') {
          drawPolygonPath(ctx, width, height, shapeElement.polygonPoints || []);
          ctx.stroke();
        } else {
          const scaledRadius = shapeElement.borderRadius * scale;
          if (scaledRadius > 0 && typeof ctx.roundRect === 'function') {
            ctx.beginPath();
            ctx.roundRect(-width / 2, -height / 2, width, height, scaledRadius);
            ctx.stroke();
          } else {
            ctx.strokeRect(-width / 2, -height / 2, width, height);
          }
        }
      }

      // Reset shadow state
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    } else if (element.elementType === 'icon') {
      const iconEl = element as IconElement;
      try {
        const iconImg = await loadIconImage(iconEl.iconName, iconEl.iconColor || '#000000');
        // object-fit: contain — icons are square by nature but allow any bbox
        const iconA = iconImg.width / iconImg.height;
        const elA = width / height;
        let dw: number, dh: number;
        if (iconA > elA) { dw = width; dh = width / iconA; }
        else { dh = height; dw = height * iconA; }
        ctx.drawImage(iconImg, -dw / 2, -dh / 2, dw, dh);
      } catch (err) {
        // Fallback: draw a dashed outline so the slot is visible in previews
        ctx.strokeStyle = iconEl.iconColor || '#999';
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(-width / 2, -height / 2, width, height);
        ctx.setLineDash([]);
      }
    } else if (element.elementType === 'text') {
      const textElement = element as TextElement;

      // Use dynamic text if available, otherwise use static content
      let displayText: string;
      if (textElement.isDynamic && dynamicTexts && dynamicTexts[element.id]) {
        // Have dynamic text available
        displayText = dynamicTexts[element.id];
      } else if (textElement.isDynamic && textElement.dynamicType) {
        // Dynamic element but no dynamic text. Prefer the template's textContent
        // when it's meaningful (not a generic placeholder like "Tagline"), so
        // posts whose AI generation failed still show something useful instead
        // of "[HEADLINE (3-8 WORDS)]". Falls back to the labeled slot only when
        // textContent is empty or an obvious placeholder.
        const PLACEHOLDER_RE = /^(headline|tagline|cta|quote|stat|text|your text|body paragraph|your brand message)\s*$/i;
        const tc = textElement.textContent?.trim() || '';
        if (tc && !PLACEHOLDER_RE.test(tc)) {
          displayText = tc;
        } else {
          const typeInfo = DYNAMIC_TEXT_LIMITS[textElement.dynamicType as DynamicTextType];
          displayText = typeInfo ? `[${typeInfo.label}]` : tc;
        }
      } else {
        // Static text element - use textContent
        displayText = textElement.textContent;
      }

      // Brand font override
      let effectiveFont = textElement.fontFamily;
      if ((options.colorMode === 'brand' || options.colorMode === 'adaptive') && options.brandFonts?.length) {
        const dynamicType = textElement.dynamicType;
        if (dynamicType === 'headline' || dynamicType === 'cta' || dynamicType === 'stat') {
          effectiveFont = options.brandFonts[0];
        } else if (dynamicType === 'tagline' || dynamicType === 'quote') {
          effectiveFont = options.brandFonts[1] || options.brandFonts[0];
        }
        // Ensure brand font is loaded
        await ensureFontLoaded(effectiveFont);
      }

      // Scale fontSize and outlineWidth proportionally with canvas
      const scaledFontSize = textElement.fontSize * scale;
      const scaledOutlineWidth = textElement.outlineWidth ? textElement.outlineWidth * scale : undefined;
      const scaledLetterSpacing = textElement.letterSpacing ? textElement.letterSpacing * scale : undefined;

      // Render text with auto-shrink, letter spacing, and line height
      renderText(
        ctx,
        displayText,
        width,
        height,
        effectiveFont,
        scaledFontSize,
        textElement.fontWeight,
        textElement.textColor,
        textElement.textAlign,
        textElement.autoSize !== false, // default true
        textElement.wrapStyle || 'normal',
        textElement.outlineColor,
        scaledOutlineWidth,
        scaledLetterSpacing,
        textElement.lineHeight,
        textElement.textShadowEnabled
      );
    }

    ctx.restore();
  }
};

/**
 * Render a composite image with template overlay
 * Returns the canvas element for further operations
 */
/**
 * For split templates, find the opaque full-width shape and return the photo zone
 * (the area NOT covered by the shape).
 */
const getSplitPhotoRegion = (template: Template, canvasW: number, canvasH: number): ClipRegion | undefined => {
  if (template.layoutType !== 'split') return undefined;

  // Find the primary shape: full-width (>=95%), opaque (opacity=1), solid color (no rgba alpha)
  const shape = template.elements.find((el) => {
    if (el.elementType !== 'shape') return false;
    const s = el as ShapeElement;
    if (el.transform.width < 95 || el.opacity < 1) return false;
    // Check if it's a solid (non-transparent) color
    const bg = s.backgroundColor || '';
    if (bg.includes('rgba') && !bg.includes(', 1)')) return false;
    return true;
  });

  if (!shape) return undefined;

  // Shape covers from shapeTop to shapeBottom (in canvas pixels)
  const shapeCenterY = (shape.transform.positionY / 100) * canvasH;
  const shapeHeight = (shape.transform.height / 100) * canvasH;
  const shapeTop = shapeCenterY - shapeHeight / 2;
  const shapeBottom = shapeCenterY + shapeHeight / 2;

  // Photo zone is the area NOT covered by the shape
  const spaceAbove = shapeTop;
  const spaceBelow = canvasH - shapeBottom;

  if (spaceBelow > spaceAbove) {
    // Shape is on top → photo goes below
    return { x: 0, y: shapeBottom, width: canvasW, height: spaceBelow };
  } else {
    // Shape is on bottom → photo goes above
    return { x: 0, y: 0, width: canvasW, height: spaceAbove };
  }
};

export const renderComposite = async (
  imageUrl: string,
  template: Template,
  options: RenderOptions = {}
): Promise<HTMLCanvasElement> => {
  const { scale = 1, backgroundOffsetX = 0, backgroundOffsetY = 0, backgroundScale = 1.0 } = options;

  // Preload any Google Fonts used by template elements
  await ensureTemplateFontsLoaded(template.elements);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  canvas.width = template.canvasWidth * scale;
  canvas.height = template.canvasHeight * scale;

  // Plan 002: if the template has an image element that references the post
  // image, DON'T fill the full canvas with the photo — instead pass it via
  // options.postImageUrl and let the element itself render it. The rest of
  // the canvas becomes the template background color.
  const hasPostImageElement = template.elements.some(
    (el) => el.elementType === 'image' && (el as ImageElement).assetReferenceType === 'post_image'
  );
  // TEMP DEBUG: log detection result for plan 002 (remove after verification)
  // eslint-disable-next-line no-console
  console.log('[plan002 debug]', template.name, 'hasPostImageElement=', hasPostImageElement,
    'imageElements=', template.elements.filter(el => el.elementType === 'image').map(el => ({
      id: el.id, assetReferenceType: (el as ImageElement).assetReferenceType
    })));

  if (hasPostImageElement) {
    // Fill with template background color only; photo is rendered inside the element
    ctx.fillStyle = template.backgroundColor || '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await drawTemplateElements(ctx, canvas, template, {
      ...options,
      postImageUrl: imageUrl,
    });
  } else {
    // Legacy behavior: photo fills canvas, elements stack on top
    const photoRegion = !isPastelUri(imageUrl) ? getSplitPhotoRegion(template, canvas.width, canvas.height) : undefined;
    await drawBackground(ctx, canvas, imageUrl, template.backgroundColor || '#ffffff', backgroundOffsetX, backgroundOffsetY, photoRegion, backgroundScale);
    await drawTemplateElements(ctx, canvas, template, options);
  }

  return canvas;
};

/**
 * Render composite and return as Blob.
 * Use `format` and `quality` to control output size:
 *   - 'image/png' (default): lossless, large
 *   - 'image/jpeg' with quality 0..1: smaller, lossy
 * Combine with `options.scale` to downscale resolution.
 */
export const renderCompositeAsBlob = async (
  imageUrl: string,
  template: Template,
  options: RenderOptions = {},
  format: 'image/png' | 'image/jpeg' = 'image/png',
  quality: number = 0.92
): Promise<Blob | null> => {
  try {
    const canvas = await renderComposite(imageUrl, template, options);
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), format, quality);
    });
  } catch (err) {
    console.error('Failed to render composite:', err);
    return null;
  }
};

/**
 * Render composite thumbnail and return as data URL
 * Renders at full resolution first, then scales down for consistency
 */
export const renderCompositeThumbnail = async (
  imageUrl: string,
  template: Template,
  thumbnailWidth: number = 400,
  options: RenderOptions = {}
): Promise<string | null> => {
  try {
    // Render at full resolution first (scale=1)
    const fullCanvas = await renderComposite(imageUrl, template, { ...options, scale: 1 });

    // Calculate thumbnail dimensions maintaining aspect ratio
    const aspectRatio = template.canvasHeight / template.canvasWidth;
    const thumbnailHeight = thumbnailWidth * aspectRatio;

    // Create thumbnail canvas and scale down
    const thumbCanvas = document.createElement('canvas');
    thumbCanvas.width = thumbnailWidth;
    thumbCanvas.height = thumbnailHeight;
    const thumbCtx = thumbCanvas.getContext('2d');
    if (!thumbCtx) throw new Error('Failed to get thumbnail canvas context');

    // Draw full resolution canvas scaled down to thumbnail
    thumbCtx.drawImage(fullCanvas, 0, 0, thumbnailWidth, thumbnailHeight);

    return thumbCanvas.toDataURL('image/jpeg', 0.8);
  } catch (err) {
    console.error('Failed to render thumbnail composite:', err);
    return null;
  }
};

/**
 * Render to an existing canvas element (useful for modal preview)
 */
export const renderToCanvas = async (
  targetCanvas: HTMLCanvasElement,
  imageUrl: string,
  template: Template,
  options: RenderOptions = {}
): Promise<void> => {
  const { backgroundOffsetX = 0, backgroundOffsetY = 0, backgroundScale = 1.0 } = options;

  await ensureTemplateFontsLoaded(template.elements);

  const ctx = targetCanvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  targetCanvas.width = template.canvasWidth;
  targetCanvas.height = template.canvasHeight;

  const photoRegion = !isPastelUri(imageUrl) ? getSplitPhotoRegion(template, targetCanvas.width, targetCanvas.height) : undefined;

  await drawBackground(ctx, targetCanvas, imageUrl, template.backgroundColor || '#ffffff', backgroundOffsetX, backgroundOffsetY, photoRegion, backgroundScale);
  await drawTemplateElements(ctx, targetCanvas, template, { ...options, scale: 1 });
};

/**
 * Render composite at a specific preview aspect ratio
 * Crops the result to the target aspect ratio using center-crop (object-fit: cover behavior)
 */
export const renderCompositeAtAspectRatio = async (
  imageUrl: string,
  template: Template,
  targetAspectRatio: number,
  options: RenderOptions = {}
): Promise<HTMLCanvasElement> => {
  // First render at full template dimensions
  const fullCanvas = await renderComposite(imageUrl, template, options);

  // Calculate crop dimensions for center-crop behavior
  const fullAspect = fullCanvas.width / fullCanvas.height;
  let cropWidth: number, cropHeight: number, cropX: number, cropY: number;

  if (targetAspectRatio > fullAspect) {
    // Target is wider - crop top/bottom
    cropWidth = fullCanvas.width;
    cropHeight = Math.round(fullCanvas.width / targetAspectRatio);
    cropX = 0;
    cropY = Math.round((fullCanvas.height - cropHeight) / 2);
  } else {
    // Target is taller - crop left/right
    cropHeight = fullCanvas.height;
    cropWidth = Math.round(fullCanvas.height * targetAspectRatio);
    cropX = Math.round((fullCanvas.width - cropWidth) / 2);
    cropY = 0;
  }

  // Create cropped canvas
  const croppedCanvas = document.createElement('canvas');
  croppedCanvas.width = cropWidth;
  croppedCanvas.height = cropHeight;
  const ctx = croppedCanvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  ctx.drawImage(
    fullCanvas,
    cropX, cropY, cropWidth, cropHeight,
    0, 0, cropWidth, cropHeight
  );

  return croppedCanvas;
};

/**
 * Render thumbnail at a specific preview aspect ratio
 */
export const renderCompositeThumbnailAtAspectRatio = async (
  imageUrl: string,
  template: Template,
  targetAspectRatio: number,
  thumbnailWidth: number = 400,
  options: RenderOptions = {}
): Promise<string | null> => {
  try {
    const croppedCanvas = await renderCompositeAtAspectRatio(
      imageUrl, template, targetAspectRatio, options
    );

    // Scale down to thumbnail maintaining the target aspect ratio
    const thumbnailHeight = Math.round(thumbnailWidth / targetAspectRatio);
    const thumbCanvas = document.createElement('canvas');
    thumbCanvas.width = thumbnailWidth;
    thumbCanvas.height = thumbnailHeight;
    const ctx = thumbCanvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get thumbnail canvas context');

    ctx.drawImage(croppedCanvas, 0, 0, thumbnailWidth, thumbnailHeight);
    return thumbCanvas.toDataURL('image/jpeg', 0.8);
  } catch (err) {
    console.error('Failed to render thumbnail at aspect ratio:', err);
    return null;
  }
};
